<template>
  <sf-modal
    v-if="modalOpen"
    size="l"
    class="sf-border-box"
    data-walkthrough-id="account-entries-modal"
    inner-modal-classes="sf-overflow-hidden"
    :footer-padding="0"
    :scrollable-content="true"
    @close="close"
  >
    <template #title>{{
      external
        ? I18n.t('account_selector_modal.select_external_accounts')
        : I18n.t('account_selector_modal.select_account')
    }}</template>

    <template #body>
      <div v-if="isLoading" class="sf-pad-3 sf-level sf-level-center">
        <div class="sf-spinner"></div>
      </div>
      <div v-else-if="external && !selectedCompany">
        <ExternalCompanySelector
          :linked-companies="linkedCompanies"
          :linked-companies-with-period="linkedCompaniesWithPeriod"
          :ledger-label="ledgerLabel"
          @company-selected="handleCompanySelected"
        />
      </div>
      <span v-else>
        <div class="sf-grid sf-grid-fluid sf-grid-gutter-medium">
          <div v-if="external" class="sf-row sf-layer-border-bottom">
            <div class="sf-col sf-sm-12 tw-mb-6">
              <div
                class="
                  sf-level
                  sf-level-between
                  sf-layer-border
                  sf-layer-rounded
                  sf-pad-2
                "
              >
                <div class="sf-level-item">
                  <div class="sf-level">
                    <div
                      class="
                        sf-level-item sf-label sf-label-subtle
                        !tw-mr-2 !tw-rounded-sm
                      "
                    >
                      <span class="icon icon-link"></span>
                    </div>
                    <div class="sf-level-item !tw-mr-2">
                      <span class="sf-font-medium sf-pad-r-1">
                        {{ selectedCompany.name }}
                      </span>
                      <span
                        v-if="selectedCompanyError"
                        v-dompurify-html="selectedCompanyError"
                        v-tooltip.html="
                          I18n.t(
                            'account_selector_modal.missing_external_period_error_tooltip',
                            { company: selectedCompany.name }
                          )
                        "
                        class="sf-label sf-label-danger sf-label-active"
                      >
                      </span>
                      <span v-else class="sf-label sf-label-success">
                        {{
                          I18n.t(
                            'account_selector_modal.selected_company_label'
                          )
                        }}
                      </span>
                      <div v-if="selectedCompany.lastSyncedAt">
                        <i class="icon-refresh sf-font-xs sf-c-text-light"></i>
                        <span class="sf-c-text-light sf-font-xs">
                          {{
                            I18n.t('account_selector_modal.last_synced_on', {
                              date: formatDate(selectedCompany.lastSyncedAt),
                            })
                          }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="sf-level-item">
                  <button
                    class="sf-btn sf-btn-small"
                    :onClick="handleSwitchCompany"
                  >
                    <span class="icon icon-exchange"></span>
                    {{
                      I18n.t(
                        'account_selector_modal.switch_company_button_label'
                      )
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="sf-row">
            <div class="sf-col sf-sm-6">
              <div class="sf-level sf-level-between sf-below-1 sf-hover">
                <div class="sf-uppercase sf-pad-l-3">
                  {{ I18n.t('account_selector_modal.selected_accounts') }}
                </div>
              </div>
              <div
                class="sf-tab sf-overflow-scroll"
                style="
                  height: calc(-308px + 100vh);
                  min-height: 452px;
                  max-height: 652px;
                "
                data-testid="selected-accounts-tab"
              >
                <div
                  v-for="account in selectedAccounts"
                  :key="account.id"
                  class="sf-tab-item sf-level sf-hover"
                  data-test="selected-account"
                >
                  <div class="fa fa-check sf-c-success"></div>
                  <div class="sf-level sf-level-between sf-full">
                    <div>
                      <div
                        v-if="showOriginalName(account)"
                        class="sf-c-text-light sf-below-1"
                      >
                        {{ account.originalNumberWithOriginalName }}
                      </div>
                      <span class="sf-fwdb sf-c-text-dark">{{
                        account.numberWithName
                      }}</span>
                    </div>
                    <a
                      class="sf-btn sf-btn-small sf-hover-item"
                      @click="deselectAccount(account)"
                      >{{ I18n.t('common.remove') }}</a
                    >
                  </div>
                </div>
              </div>
            </div>
            <div class="sf-col sf-sm-6">
              <div class="sf-level sf-level-between sf-below-1">
                <div class="sf-uppercase sf-pad-l-3">
                  {{ I18n.t('account_selector_modal.available_accounts') }}
                </div>
              </div>
              <div
                class="
                  sf-level
                  sf-below-2
                  sf-layer-rounded
                  sf-bg-snow
                  sf-pad-v-1
                  sf-pad-h-2
                "
              >
                <input
                  v-model="searchText"
                  class="sf-form-input-transparent sf-full"
                  data-test="search-text"
                  placeholder="Filter"
                  type="text"
                />
                <a class="sf-btn sf-btn-small" @click="selectFiltered">{{
                  I18n.t('account_selector_modal.select_all')
                }}</a>
              </div>
              <div
                class="sf-tab sf-overflow-scroll"
                style="
                  height: calc(-360px + 100vh);
                  min-height: 400px;
                  max-height: 600px;
                "
              >
                <div
                  v-for="account in filteredAccounts"
                  :key="account.id"
                  class="sf-tab-item sf-level"
                  data-test="available-account"
                >
                  <div class="sf-level sf-level-between sf-full">
                    <div>
                      <div
                        v-if="showOriginalName(account)"
                        class="sf-c-text-light sf-below-1"
                      >
                        {{ account.originalNumberWithOriginalName }}
                      </div>
                      <span class="sf-fwdb sf-c-text">{{
                        account.numberWithName
                      }}</span>
                    </div>
                    <a
                      class="sf-btn sf-btn-small"
                      @click="selectAccount(account)"
                      >{{ I18n.t('common.select') }}</a
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </span>
    </template>

    <template #footer>
      <div
        class="sf-pad-h-4 sf-pad-v-2 tw-w-full"
        :class="selectedCompany ? 'sf-bg-warning-subtle' : ''"
      >
        <div class="sf-level sf-level-between">
          <div class="sf-level">
            <span
              v-if="isDefaultChanged"
              class="sf-c-warning fa fa-warning"
            ></span>
            <div v-if="isDefaultChanged" class="sf-c-warning-darker">
              {{ I18n.t('account_selector_modal.changed_from_default') }}
            </div>
            <a
              v-if="isDefaultChanged"
              class="sf-btn sf-btn-small"
              @click="restoreDefault"
              >{{ I18n.t('account_selector_modal.restore_default') }}</a
            ><a
              v-if="canDeselect"
              class="sf-btn sf-btn-small"
              @click="deselectAll"
              ><span>{{ I18n.t('account_selector_modal.clear_all') }}</span></a
            >
          </div>
          <div class="sf-level">
            <a class="sf-btn" @click="close">{{
              I18n.t('common.form.cancel')
            }}</a>
            <a
              class="sf-btn"
              :class="isOnCompanySelection ? 'sf-disabled' : 'sf-btn-success'"
              @click="save"
              >{{ I18n.t('common.save') }}</a
            >
          </div>
        </div>
      </div>
    </template>
  </sf-modal>
</template>

<script>
import api from '../../../../templates/api/index'
import { doesAccountNameSatisfyQuery } from '../../../../utils/account-search-helpers'
import { SfModal } from '@app/shared/vue/components/sf-modal'
import _ from 'lodash'
import ExternalCompanySelector from './components/external-company-selector'
import formatTimestamp from '@app/utils/time-formatter'
import { SfLog } from '@app/adapters/logger'

export default {
  components: {
    SfModal,
    ExternalCompanySelector,
  },
  data() {
    return {
      modalOpen: true,
      isLoading: false,
      selectedAccounts: [],
      otherAccounts: [],
      defaultAccounts: [],
      updatePath: null,
      afterUpdate: null,
      updateParams: {},
      searchText: '',
      range: null,
      defaultValue: null,
      value: null,
      // Data specific to the "external account selector":
      external: false,
      linkedCompanies: [],
      selectedCompany: null,
      linkedCompaniesWithPeriod: [],
      ledgerLabel: null,
    }
  },

  computed: {
    valueToSubmit() {
      if (this.defaultAccounts.length && !this.isDefaultChanged) {
        return ''
      } else if (this.selectedAccounts.length) {
        return this.selectedAccounts.map((value) => `#${value.id}`).join(',')
      } else {
        return '~!'
      }
    },

    filteredAccounts() {
      const accountsToShow = this.searchText
        ? this.otherAccounts.filter((account) =>
            this.isAccountSatisfyQuery(account, this.searchText)
          )
        : this.otherAccounts

      return _.sortBy(accountsToShow, (acc) => acc.numberWithName)
    },

    isDefaultChanged() {
      const hasDifferentItems =
        this.selectedAccounts.length !== this.defaultAccounts.length ||
        _.differenceWith(
          this.selectedAccounts,
          this.defaultAccounts,
          this.compareAccount
        ).length !== 0

      return this.defaultAccounts.length && hasDifferentItems
    },

    isOnCompanySelection() {
      return this.external && !this.selectedCompany
    },

    canDeselect() {
      return this.selectedAccounts.length > 0 && !this.isOnCompanySelection
    },

    selectedCompanyError() {
      if (
        this.selectedCompany &&
        !this.linkedCompaniesWithPeriod.includes(this.selectedCompany.id)
      ) {
        return I18n.t('account_selector_modal.missing_external_period_error', {
          period: this.ledgerLabel,
        })
      } else {
        return null
      }
    },
  },

  methods: {
    openModal({
      defaultValue,
      value,
      attemptToConfirmUnreview,
      range,
      updatePath,
      afterUpdate,
      objectLedgerId,
      external,
      ledgerLabel,
      dataToSubmit,
      ...updateParams
    }) {
      this.attemptToConfirmUnreview = attemptToConfirmUnreview
      this.updatePath = updatePath
      this.updateParams = updateParams
      this.afterUpdate = afterUpdate
      this.objectLedgerId = objectLedgerId
      this.modalOpen = true
      this.isLoading = true
      this.range = range
      this.defaultValue = defaultValue
      this.value = value
      this.external = external
      this.ledgerLabel = ledgerLabel
      this.dataToSubmit = dataToSubmit

      if (external) {
        this.fetchExternalSelectorAccounts()
      } else {
        this.fetchSelectorAccounts()
      }
    },

    async fetchSelectorAccounts() {
      this.isLoading = true

      try {
        const { accounts, selectedAccounts, defaultAccounts } =
          await api.getSelectorModalAccounts({
            range: this.range,
            defaultValue: this.defaultValue,
            value: this.value,
            objectLedgerId: this.objectLedgerId,
          })
        this.selectedAccounts = selectedAccounts
        this.otherAccounts = _.differenceWith(
          accounts,
          this.selectedAccounts,
          this.compareAccount
        )
        this.defaultAccounts = defaultAccounts
        this.isLoading = false
      } catch (e) {
        SfLog.warn('Error while fetching accounts for the selector', {
          extra: {
            error: e,
          },
        })
      }
    },

    async fetchExternalSelectorAccounts(newlySelectedCompanyId = null) {
      this.isLoading = true

      try {
        const {
          accounts,
          selectedAccounts,
          defaultAccounts,
          linkedCompanies,
          selectedCompany,
          linkedCompaniesWithPeriod,
        } = await api.getExternalSelectorModalAccounts({
          range: this.range,
          value: this.value,
          objectLedgerId: this.objectLedgerId,
          selectedCompanyId: newlySelectedCompanyId,
        })

        this.selectedAccounts = selectedAccounts
        this.otherAccounts = _.differenceWith(
          accounts,
          this.selectedAccounts,
          this.compareAccount
        )
        this.defaultAccounts = defaultAccounts
        this.linkedCompanies = linkedCompanies
        this.selectedCompany = selectedCompany
        this.linkedCompaniesWithPeriod = linkedCompaniesWithPeriod
        this.isLoading = false
      } catch (e) {
        SfLog.warn('Error while fetching external accounts for the selector', {
          extra: {
            error: e,
          },
        })
      }
    },

    close() {
      this.modalOpen = false
      this.searchText = ''
    },

    save() {
      // attemptToConfirmUnreview is used to skip the confirmation when saving
      // data on the proposal edit page
      if (this.attemptToConfirmUnreview) {
        this.saveWithUnreviewConfirmation()
      } else {
        return this.performSave()
      }
    },

    saveWithUnreviewConfirmation() {
      window.eventHub.$emit('confirmUnreviewTemplate', {
        callback: () => {
          this.performSave()
        },
      })
    },

    performSave() {
      const properties = {
        properties: [
          {
            ...this.updateParams,
            objectLedgerId: this.objectLedgerId,
            value: this.valueToSubmit,
          },
          ...this.dataToSubmit,
        ],
      }

      this.isLoading = true
      api
        .updateTextProperties({ updatePath: this.updatePath, properties })
        .then((response) => {
          Silverfin.Spinner.success()
          this.close()
          this.afterUpdate(response)
          window.eventHub.$emit('reloadWorkflowReviewStatusData')
        })
        .catch(() => {
          Silverfin.Spinner.error()
        })
        .then(() => {
          this.isLoading = false
        })
    },
    selectFiltered() {
      this.filteredAccounts.forEach(this.selectAccount)
      this.searchText = ''
    },

    deselectAll() {
      // Since we're mutating selectedAccounts we need to create new array out of it first
      ;[...this.selectedAccounts].forEach(this.deselectAccount)
    },

    restoreDefault() {
      const accountsToDeselect = _.differenceWith(
        this.selectedAccounts,
        this.defaultAccounts,
        this.compareAccount
      )
      const accountsToSelect = _.differenceWith(
        this.defaultAccounts,
        this.selectedAccounts,
        this.compareAccount
      )

      accountsToDeselect.forEach(this.deselectAccount)
      accountsToSelect.forEach(this.selectAccount)
    },

    selectAccount(account) {
      const accountIndex = this.otherAccounts.indexOf(account)
      this.otherAccounts.splice(accountIndex, 1)
      this.selectedAccounts.push(account)
    },

    deselectAccount(account) {
      const accountIndex = this.selectedAccounts.indexOf(account)
      this.selectedAccounts.splice(accountIndex, 1)
      this.otherAccounts.push(account)
    },

    showOriginalName(account) {
      return account.originalNumberWithOriginalName !== account.numberWithName
    },

    compareAccount(account, otherAccount) {
      return account.id === otherAccount.id
    },

    formatDate(date) {
      return formatTimestamp(date, {
        shouldUseLocalTimezone: true,
        format: 'D MMM Y, HH:mm z',
      })
    },

    isAccountSatisfyQuery(account, query) {
      return (
        doesAccountNameSatisfyQuery(account.numberWithName, query) ||
        doesAccountNameSatisfyQuery(
          account.originalNumberWithOriginalName,
          query
        )
      )
    },

    handleCompanySelected(company) {
      this.fetchExternalSelectorAccounts(company.id)
    },

    handleSwitchCompany() {
      this.selectedCompany = null
    },
  },
}
</script>
